import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "@/i18n-js/instance";
import { usePlanTrialBanner } from "@/react/components/Layout/TrialBanner/usePlanTrialBanner";
import { useCurrentPlanApi } from "@/react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import type {
  PlanSettingsEventType,
  SpecificProperties,
} from "@/react/hooks/analytics/types";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";
import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { usePunditUserContext } from "@circle-react/contexts";

/* eslint-disable @typescript-eslint/no-unused-vars -- Imported to avoid knip unused export warnings */
// @ts-expect-error TS6133: Imported to avoid knip unused export warnings
// eslint-disable-next-line sonarjs/unused-import -- Disabled for plugin version update. Fix when possible
import { useCreatePlanTrial } from "@circle-react/hooks/planTrials/useCreatePlanTrial";

/* eslint-enable @typescript-eslint/no-unused-vars -- Re-enable unused vars checking */
import { useDestroyPlanTrial } from "@circle-react/hooks/planTrials/useDestroyPlanTrial";
import { Button } from "@circle-react-uikit/Button";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { Loader } from "../../Loader";
import { useCanDowngrade } from "../../hooks/useCanDowngrade";
import { BlockingFeatures } from "../BlockingFeatures";

export const ManagePlanTrialModalComponent = () => {
  const modal = useModal();
  const [isCancelled, setIsCancelled] = useState(false);
  const { refetch: refetchPundit } = usePunditUserContext();
  const { refetchCurrentPlan } = useCurrentPlanApi();
  const {
    planTrialEndDateFormatted,
    planTrialDaysRemainingInWords,
    planTrialDaysLeft,
    planTrial,
  } = usePlanTrialBanner();
  const { currentPlan } = useCurrentPlanApi();
  const { trackEvent } = useEventsTracker();

  const planId = currentPlan?.id;
  const { data: canDowngradeData, isLoading: isCheckingDowngrade } =
    useCanDowngrade(planId || 0);

  const baseEventProps = useMemo<SpecificProperties>(
    () => ({
      days_left: planTrialDaysLeft,
      trial_type: planTrial?.trial_type,
      plan_name: currentPlan?.display_name,
    }),
    [planTrialDaysLeft, planTrial?.trial_type, currentPlan?.display_name],
  );

  const trackModalEvent = useCallback(
    (
      eventName: PlanSettingsEventType,
      additionalProps: SpecificProperties = {},
    ) => {
      const eventProps: SpecificProperties = {
        ...baseEventProps,
        ...additionalProps,
      };
      trackEvent(eventName, eventProps);
    },
    [baseEventProps, trackEvent],
  );

  useEffect(() => {
    if (modal.visible && !isCheckingDowngrade && !isCancelled) {
      trackModalEvent("manage_plan_trial_modal.opened", {
        cannot_downgrade: canDowngradeData
          ? !canDowngradeData.can_downgrade
          : false,
      });
    }
  }, [
    modal.visible,
    isCheckingDowngrade,
    canDowngradeData,
    isCancelled,
    trackModalEvent,
  ]);

  const hasFiredCancelSuccessful = useRef(false);

  useEffect(() => {
    if (isCancelled && modal.visible && !hasFiredCancelSuccessful.current) {
      trackModalEvent("manage_plan_trial_modal.cancel_successful");
      hasFiredCancelSuccessful.current = true;
    }
  }, [isCancelled, modal.visible, trackModalEvent]);

  const { mutateAsync: destroyPlanTrial, isLoading } = useDestroyPlanTrial({
    onSuccess: async () => {
      setIsCancelled(true);
      await refetchPundit();
      await refetchCurrentPlan();
    },
    onError: error => {
      trackModalEvent("manage_plan_trial_modal.cancel_error", {
        error_message: error?.message || "Unknown error",
      });
    },
  });

  const handleKeepTrial = async () => {
    trackModalEvent("manage_plan_trial_modal.keep_trial.clicked");
    await modal.hide();
    modal.remove();
  };

  const handleCancelTrial = async () => {
    trackModalEvent("manage_plan_trial_modal.cancel_trial.clicked", {
      cannot_downgrade: canDowngradeData && !canDowngradeData.can_downgrade,
    });
    await destroyPlanTrial();
  };

  const handleBackToCommunity = async () => {
    trackModalEvent("manage_plan_trial_modal.back_to_community.clicked");
    await modal.hide();
    modal.remove();
  };

  const handleClose = async () => {
    trackModalEvent("manage_plan_trial_modal.closed", {
      was_cancelled: isCancelled,
    });
    await modal.hide();
    modal.remove();
  };

  if (isCheckingDowngrade || (isLoading && !isCancelled)) {
    return (
      <Modal
        title={t("manage_plan_trial_modal.title")}
        isOpen={modal.visible}
        onClose={handleClose}
      >
        <Modal.Overlay />
        <Modal.Content size="md">
          <Loader
            title={
              isLoading
                ? t("manage_plan_trial_modal.canceling_trial")
                : t("pricing_plans.upgrade_modal.loading.validating_limits")
            }
          />
        </Modal.Content>
      </Modal>
    );
  }

  const hasBlockers = canDowngradeData && !canDowngradeData.can_downgrade;

  if (isCancelled) {
    return (
      <Modal
        title={t("manage_plan_trial_modal.cancelled_title")}
        isOpen={modal.visible}
        onClose={handleClose}
      >
        <Modal.Overlay />
        <Modal.Content size="md">
          <Modal.Header>
            <Modal.Title>
              {t("manage_plan_trial_modal.cancelled_title")}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton onClick={handleClose} />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body>
            <div className="py-6 text-center">
              <Typography.BodyLg>
                {t("manage_plan_trial_modal.cancelled_message", {
                  planName: currentPlan?.display_name,
                })}
              </Typography.BodyLg>
            </div>
          </Modal.Body>
          <Modal.Footer className="mt-5 flex justify-center">
            <Button variant="primary" large onClick={handleBackToCommunity}>
              {t("manage_plan_trial_modal.back_to_community_button")}
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    );
  }

  return (
    <Modal
      title={t("manage_plan_trial_modal.title")}
      isOpen={modal.visible}
      onClose={handleClose}
    >
      <Modal.Overlay />
      <Modal.Content size="md">
        <Modal.Header>
          <Modal.Title>{t("manage_plan_trial_modal.title")}</Modal.Title>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={handleClose} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-6 space-y-4">
            <Typography.BodyMd>
              {t("manage_plan_trial_modal.trial_description", {
                daysRemaining: planTrialDaysRemainingInWords,
                trialEndDate: planTrialEndDateFormatted,
              })}
            </Typography.BodyMd>

            {hasBlockers &&
              canDowngradeData?.blocking_features &&
              canDowngradeData.blocking_features.length > 0 && (
                <BlockingFeatures
                  blockingFeatures={canDowngradeData.blocking_features}
                  i18nRoot="pricing_plans.upgrade_modal.interval_switch"
                />
              )}
          </div>
        </Modal.Body>
        <Modal.Footer className="mt-5 flex justify-end gap-x-3">
          <Button
            variant="secondary"
            large
            onClick={handleKeepTrial}
            disabled={isLoading}
          >
            {t("manage_plan_trial_modal.keep_trial_button")}
          </Button>
          <Button
            variant="primary"
            large
            onClick={handleCancelTrial}
            disabled={isLoading || hasBlockers}
          >
            {isLoading
              ? t("manage_plan_trial_modal.canceling")
              : t("manage_plan_trial_modal.cancel_trial_button")}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
